/**
 * Lauf-Aktionen in der Historie (Matze 15.10.2026): aktivieren und deaktivieren — je Lauf,
 * mit Tippbestätigung, weil beides ins echte Plenty schreibt. Die Druck-Bestelleigenschaften
 * setzt die Anlage selbst (`eigenschaften/druck.ts`), hier gibt es dafür keinen Knopf.
 *
 * Aktivieren folgt der festen Regel: Artikel ohne Kindvarianten → Hauptvariante aktiv;
 * mit Kindvarianten → nur die Kinder, die Hauptvariante bleibt aus. Danach setzt der
 * Server das Cross-Selling im Hintergrund neu (erst dann dürfen die Artikel Partner sein).
 * Alle Pfade OHNE `/api/`-Prefix — der Core-api-Client hängt ihn selbst an.
 */
import { useState } from 'react';
import { Power, PowerOff } from 'lucide-react';
import { api } from '@libero/core/services/api';
import LaufButton from './LaufButton';
import Info from './Info';
import { zahl } from '../lib/format';
import type { HistorieLauf } from '../lib/typen';

interface AktivierBericht { aktiviert: number; bleibtInaktiv: number; fehler: number; meldungen: string[] }

function Bestaetigung({ wort, wert, setzen }: { wort: string; wert: string; setzen: (w: string) => void }) {
  return (
    <label className="flex flex-col text-xs text-[#6B6A66]">
      Zum Bestätigen <b className="text-[#1A1A1A]">{wort}</b> tippen
      <input
        value={wert}
        onChange={e => setzen(e.target.value)}
        placeholder={wort}
        className="mt-1 w-36 rounded border border-[#DAD9D9] px-2 py-1 text-sm"
      />
    </label>
  );
}

export default function LaufAktionen({ lauf, aufNeuLaden, aufPruefen }: {
  lauf: HistorieLauf;
  /** Historie neu laden. */
  aufNeuLaden: () => void;
  /** „In Plenty prüfen" für diesen Lauf — zeigt danach aktiv/inaktiv je Artikel. */
  aufPruefen: () => void;
}) {
  const [aktiv, setAktiv] = useState('');
  const [aus, setAus] = useState('');
  const [arbeitet, setArbeitet] = useState<'an' | 'aus' | null>(null);
  const [meldung, setMeldung] = useState<{ text: string; fehler: boolean } | null>(null);

  const bericht = (b: AktivierBericht, was: string) => [
    `${was}: ${zahl(b.aktiviert)} Varianten aktiviert`,
    b.bleibtInaktiv > 0 ? `${zahl(b.bleibtInaktiv)} bleiben aus` : null,
    b.fehler > 0 ? `${zahl(b.fehler)} Fehler` : null,
    ...b.meldungen,
  ].filter(Boolean).join(' · ');

  const ausfuehren = async (art: 'an' | 'aus') => {
    setArbeitet(art);
    setMeldung(null);
    try {
      if (art === 'an') {
        const r = await api.post<{ data: AktivierBericht }>(`/programmes/artikelimport/laeufe/${lauf.id}/aktivieren`, {});
        setMeldung({ text: bericht(r.data.data, 'Aktiviert'), fehler: r.data.data.fehler > 0 });
        setAktiv('');
        aufPruefen();
      } else {
        const r = await api.post<{ data: AktivierBericht }>(`/programmes/artikelimport/laeufe/${lauf.id}/rollback`, { bestaetigung: 'DEAKTIVIEREN' });
        const b = r.data.data;
        setMeldung({ text: `Deaktiviert: ${zahl(b.bleibtInaktiv)} Varianten${b.fehler > 0 ? `, ${zahl(b.fehler)} Fehler` : ''}. ${b.meldungen.join(' ')}`, fehler: b.fehler > 0 });
        setAus('');
        aufPruefen();
      }
    } catch (e: any) {
      setMeldung({ text: e?.response?.data?.error?.message ?? 'Fehlgeschlagen.', fehler: true });
    } finally {
      setArbeitet(null);
    }
  };

  return (
    <div className="mt-4 space-y-3 rounded border border-[#DAD9D9] bg-white p-3">
      <h3 className="flex items-center gap-2 text-sm font-medium text-[#1A1A1A]">
        <Power className="h-4 w-4" aria-hidden /> Aktivieren
        <Info titel="Was wird aktiv?">
          <p>Artikel ohne Varianten: die Hauptvariante wird aktiv.</p>
          <p>Artikel mit Varianten: nur die Varianten werden aktiv, die Hauptvariante bleibt aus.</p>
          <p>Unvollständig angelegte und in Plenty gelöschte Artikel bleiben außen vor.</p>
          <p>Danach setzt Libero das Cross-Selling neu — erst aktive Artikel dürfen Partner sein, so füllen sich auch die Teamlines.</p>
          <p>Einzelne Artikel lassen sich in der Liste darunter aktivieren.</p>
        </Info>
      </h3>
      <div className="flex flex-wrap items-end gap-6">
        <div className="flex items-end gap-2">
          <Bestaetigung wort="AKTIVIEREN" wert={aktiv} setzen={setAktiv} />
          <LaufButton
            laeuft={arbeitet === 'an'}
            laufText="Aktiviere …"
            onClick={() => void ausfuehren('an')}
            disabled={aktiv !== 'AKTIVIEREN' || arbeitet != null}
            size="sm"
          >
            Alle aktivieren
          </LaufButton>
        </div>
        <div className="flex items-end gap-2">
          <Bestaetigung wort="DEAKTIVIEREN" wert={aus} setzen={setAus} />
          <LaufButton
            laeuft={arbeitet === 'aus'}
            laufText="Deaktiviere …"
            onClick={() => void ausfuehren('aus')}
            disabled={aus !== 'DEAKTIVIEREN' || arbeitet != null}
            variant="outline"
            size="sm"
          >
            <PowerOff className="mr-1 h-3.5 w-3.5" aria-hidden /> Alle deaktivieren
          </LaufButton>
        </div>
      </div>

      {meldung && <p className={`text-sm ${meldung.fehler ? 'text-red-800' : 'text-green-800'}`}>{meldung.text}</p>}
    </div>
  );
}
